import { Injectable } from '@angular/core';
import { AuthenticationState, ExpirationSnapshot } from './authentication-state';
import { UrlBuilder } from '../core/url-builder/url-builder';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Subscription } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { Settings } from '../core/settings';
import { MdlDialogService, MdlDialogReference } from '@angular-mdl/core';
import { TimeoutWarningComponent } from './timeout-warning.component';
import { TimeoutWarningState } from './timeout-warning-state';

@Injectable()
export class IdleUserMonitor {

    private monitoringInProgress = false;
    private expirationSubscription: Subscription;
    private tokenInfoSubscription: Subscription;

    private onIdleEndSubscription: Subscription;
    private onTimeoutSubscription: Subscription;
    private onTimeoutWarningSubscription: Subscription;

    private warningDialog: Promise<MdlDialogReference> | null = null;

    private timeoutWarningState: TimeoutWarningState = new TimeoutWarningState();

    constructor(
        private settings: Settings,
        private authenticationState: AuthenticationState,
        private urlBuilder: UrlBuilder,
        private idle: Idle,
        private keepalive: Keepalive,
        private dialogService: MdlDialogService
    ) {

        this.idle.setIdle(this.settings.inactivityPeriodSeconds);

        this.idle.setTimeout(this.settings.timeoutPeriodSeconds);

        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        this.keepalive.interval(this.settings.inactivityPeriodSeconds / 3);

        this.onIdleEndSubscription = this.idle.onIdleEnd.subscribe(this.onIdleEnd);
        this.onTimeoutWarningSubscription = this.idle.onTimeoutWarning.subscribe(this.onTimeoutWarning);
        this.onTimeoutSubscription = this.idle.onTimeout.subscribe(this.onTimeout);

        this.keepalive.onPing.subscribe(this.refreshSession);
    }

    public start() {
        if (this.monitoringInProgress) {
            throw new Error('Monitoring is already in progress');
        }

        this.monitoringInProgress = true;

        this.expirationSubscription = this.authenticationState
            .tokenExpiration
            .subscribe(this.checkExpirationStatus);

        this.authenticationState
            .authInfo
            .pipe(
                map(x => x.isAuthenticated),
                distinctUntilChanged()
            )
            .subscribe(this.handleAuthenticationStatusChange);
    }

    public async stop() { 
        await this.hideWarningDialog();

        if (this.monitoringInProgress) {
            throw new Error('Monitoring was not started');
        }

        this.expirationSubscription.unsubscribe();
        this.tokenInfoSubscription.unsubscribe();

        this.monitoringInProgress = false;
    }

    public dispose() {
        if (this.monitoringInProgress) {
            this.stop();
        }

        this.onIdleEndSubscription.unsubscribe();
        this.onTimeoutSubscription.unsubscribe();
        this.onTimeoutWarningSubscription.unsubscribe();
    }

    private handleAuthenticationStatusChange = (x: boolean) => {
        if (x) {
            this.idle.watch();
        } else {
            this.idle.stop();
        }
    }

    private checkExpirationStatus = async (x: ExpirationSnapshot) => {
        if (x.isExpired) {
            console.debug('Token has expired, redireting to login page');
            await this.hideWarningDialog();
            this.urlBuilder.esa().login().reload({ saveReturnUrl: true });
        }
    }

    private onIdleEnd = async () => {
        console.debug('user is not idle anymore');
        await this.hideWarningDialog();
    }

    ///Show timeout notification
    private onTimeoutWarning = (secondsLeft: number) => {
        this.timeoutWarningState.setMillisecondsLeft(secondsLeft * 1000);
        if (!this.warningDialog) {
            this.warningDialog = this.dialogService.showCustomDialog({
                component: TimeoutWarningComponent,
                providers: [ { provide: TimeoutWarningState, useValue: this.timeoutWarningState } ]
            }).toPromise();
        }
    }

    ///Logout user without confirmation
    private onTimeout = async () => {
        console.debug('User timed out, logging out');
        await this.hideWarningDialog();
        this.urlBuilder.esa().logout().navigate({ saveReturnUrl: true });
    }

    private async hideWarningDialog() {
        if (this.warningDialog) {
            const dialogRef = await this.warningDialog;
            dialogRef.hide();
            this.warningDialog = null;
        }
    }

    private refreshSession = () => {
        console.debug('Refreshing user session');
        this.authenticationState.refresh();
    }
}